import { useState, useRef } from "react";
import type { PostImage } from "@abejon/shared";

interface ImageCarouselProps {
  images: PostImage[];
}

export function ImageCarousel({ images }: ImageCarouselProps) {
  const [current, setCurrent] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);

  if (images.length === 0) return null;

  function goTo(index: number) {
    const clamped = Math.max(0, Math.min(images.length - 1, index));
    setCurrent(clamped);
    if (containerRef.current) {
      const child = containerRef.current.children[clamped] as HTMLElement;
      child?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    }
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      goTo(current + (diff > 0 ? 1 : -1));
    }
  }

  return (
    <div className="relative">
      {/* Image strip */}
      <div
        ref={containerRef}
        className="flex snap-x snap-mandatory overflow-x-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((img, i) => (
          <div
            key={img.id}
            className="w-full flex-shrink-0 snap-center"
            style={{ display: i === current ? "block" : "none" }}
          >
            <img
              src={img.imageUrl}
              alt=""
              className="w-full object-contain"
              style={{ maxHeight: "70vh" }}
            />
          </div>
        ))}
      </div>

      {/* Arrows (desktop) */}
      {images.length > 1 && (
        <>
          {current > 0 && (
            <button
              onClick={() => goTo(current - 1)}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
            >
              ‹
            </button>
          )}
          {current < images.length - 1 && (
            <button
              onClick={() => goTo(current + 1)}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
            >
              ›
            </button>
          )}
        </>
      )}

      {/* Dots */}
      {images.length > 1 && (
        <div className="mt-2 flex justify-center gap-1.5" role="tablist" aria-label="Image indicators">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to image ${i + 1}`}
              aria-selected={i === current}
              role="tab"
              className={`h-1.5 w-1.5 rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${
                i === current ? "bg-gray-900" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
